/**
 * The client's matters.
 *
 * Scoped like every other list: these are the matters this reader may see, which on a
 * firm with ethical walls is not the same as the matters this client has. The empty
 * state says nothing about why — a matter outside the reader's scope is absent from
 * here by design, and speculating about permissions would reopen the disclosure the
 * 404 rule closes.
 */

import { STAFF, qk, usePaginatedList, type CaseListItem, type ClientDetail } from '@legalcrm/api';
import { Badge, StatusPill, Table, statusTone, type Column } from '@legalcrm/ui';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { formatDate, humanise } from '@/lib/format';

export function ClientMattersTab({ client }: { client: ClientDetail }) {
  const navigate = useNavigate();
  const { matterLabel } = useAuth();

  const list = usePaginatedList<CaseListItem>(
    qk.sub('clients', client.id, 'matters-tab'),
    STAFF.cases,
    { client: client.id },
  );

  const columns: Column<CaseListItem>[] = [
    {
      key: 'case_number',
      header: 'Reference',
      width: 'w-36',
      render: (row) => (
        <Link
          to={`/cases/${row.id}`}
          className="font-medium text-primary hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          {row.case_number}
        </Link>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink">{row.title}</p>
          <p className="truncate text-xs text-ink-muted">{row.service_name}</p>
        </div>
      ),
    },
    {
      key: 'stage',
      header: 'Stage',
      hideOnMobile: true,
      // The firm's configured stage NAME. The key is internal vocabulary.
      render: (row) => <Badge tone="info">{row.stage_name}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusPill label={humanise(row.status)} tone={statusTone(row.status)} />,
    },
    {
      key: 'responsible_lawyer',
      header: 'Responsible',
      hideOnMobile: true,
      render: (row) =>
        row.responsible_lawyer ? (
          <span className="text-ink">{row.responsible_lawyer.name}</span>
        ) : (
          <Badge tone="warning" size="sm">
            Unassigned
          </Badge>
        ),
    },
    {
      key: 'opened_at',
      header: 'Opened',
      align: 'right',
      hideOnMobile: true,
      render: (row) => <span className="tabular-nums">{formatDate(row.opened_at)}</span>,
    },
  ];

  return (
    <Table
      caption={`${client.display_name} — ${matterLabel.toLowerCase()}s`}
      columns={columns}
      rows={list.items}
      rowKey={(row) => row.id}
      loading={list.isLoading}
      error={list.error}
      onRetry={() => void list.refetch()}
      onRowClick={(row) => navigate(`/cases/${row.id}`)}
      emptyTitle={`No ${matterLabel.toLowerCase()}s to show`}
      emptyDescription={`Nothing is filed under this client that you can see.`}
      pagination={{
        hasNext: list.hasNext,
        hasPrevious: list.hasPrevious,
        onNext: list.goNext,
        onPrevious: list.goPrevious,
        summary: `${list.items.length} shown`,
      }}
    />
  );
}
